import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Chip, CircularProgress,
  Box, Stack, Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { useSnackbar } from 'notistack'; 

import { 
  getAsyncAnonymizationJobs, 
  getAsyncValidationReport,
  deleteAsyncAnonymizationJob
} from '../../../services/asyncAnonymizationService';

import DeleteConfirmationModal from '../../../shared/DeleteConfirmationModal/DeleteConfirmationModal';
import ValidationReportModal from './ValidationReportModal';

const PIIValidationAsyncJobsList = () => {
  const { enqueueSnackbar } = useSnackbar(); 

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [fetchingReportId, setFetchingReportId] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const fetchJobs = async () => {
    try {
      const data = await getAsyncAnonymizationJobs();
      setJobs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleViewReport = async (jobId) => {
    setFetchingReportId(jobId);
    try {
      const report = await getAsyncValidationReport(jobId);
      setActiveReport(report);
    } catch (err) {
      enqueueSnackbar(`Nepodarilo sa načítať report: ${err.message}`, { variant: 'error' });
    } finally {
      setFetchingReportId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedJob) return;
    try {
      await deleteAsyncAnonymizationJob(selectedJob.id);
      setJobs((prev) => prev.filter((j) => j.id !== selectedJob.id));
      enqueueSnackbar('Záznam bol úspešne vymazaný.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Odstránenie zlyhalo: ${err.message}`, { variant: 'error' });
    } finally {
      setDeleteModalOpen(false);
    }
  };

  const getStatusChip = (status) => {
    const common = { borderRadius: 1.5, fontWeight: 600 };
    switch (status) {
      case 'done': return <Chip label="Skontrolované" color="success" size="small" variant="outlined" sx={common} />;
      case 'running': return <Chip label="Spracúva sa" color="primary" size="small" variant="outlined" sx={common} />;
      case 'pending': return <Chip label="V rade" color="warning" size="small" variant="outlined" sx={common} />;
      case 'failed': return <Chip label="Chyba" color="error" size="small" variant="outlined" sx={common} />;
      default: return <Chip label={status} size="small" variant="outlined" sx={common} />;
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        borderRadius: 3, 
        border: '1px solid', 
        borderColor: 'divider', 
        bgcolor: 'background.paper',
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column' 
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          História PDF kontrol
        </Typography>
      </Box>
      
      {loading && jobs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Paper 
          variant="outlined" 
          sx={{ 
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            p: 4, flexGrow: 1, minHeight: 250, bgcolor: 'action.hover', borderStyle: 'dashed', borderRadius: 2, boxShadow: 'none'
          }}
        >
          <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
            Zatiaľ žiadna história
          </Typography>
          <Typography variant="body2" color="text.disabled" align="center">
            Po nahratí PDF súborov do rady sa tu zobrazí ich zoznam a možnosť prezrieť si výsledný report.
          </Typography>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper} 
          variant="outlined" 
          sx={{ flexGrow: 1, borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Súbor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Dátum</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Stav</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Akcia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }} title={job.input_filename}>
                    {job.input_filename}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {new Date(job.created_at).toLocaleDateString('sk-SK')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(job.created_at).toLocaleTimeString('sk-SK')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(job.status)}
                    
                    {job.status === 'failed' && (
                      <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                        {job.error_message}
                      </Typography>
                    )}

                    {(job.status === 'running' || job.status === 'pending') && job.progress_message && (
                      <Tooltip title={job.progress_message} placement="bottom-start" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
                          <CircularProgress size={12} thickness={5} />
                          <Typography 
                            variant="caption" color="text.secondary" 
                            sx={{ fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}
                          >
                            {job.progress_message}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      <Button
                        variant={job.status === 'done' ? "contained" : "outlined"}
                        size="small"
                        startIcon={fetchingReportId === job.id ? <CircularProgress size={16} color="inherit" /> : <VisibilityIcon />}
                        disabled={job.status !== 'done' || fetchingReportId === job.id}
                        onClick={() => handleViewReport(job.id)}
                        disableElevation
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                      >
                        Výsledok
                      </Button>

                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => { setSelectedJob(job); setDeleteModalOpen(true); }}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      <ValidationReportModal 
        open={!!activeReport} 
        report={activeReport} 
        onClose={() => setActiveReport(null)} 
      />
    </Paper>
  );
};

export default PIIValidationAsyncJobsList;